/**
 * Discovery Review Tab
 * Phase 1: Approve/Reject discovered training artifacts
 * 
 * IMPORTANT: METADATA ONLY DISPLAY
 * Shows only: title, URL, thumbnail, duration, inferred domains/skill depth
 * Explicitly NO: transcripts, description text, extracted content
 * 
 * For books: Requires purchase URL or reference-only flag before approval
 */

import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  usePendingDiscoveries,
  useApproveDiscovery,
  useRejectDiscovery,
  useDiscoveryStats,
  useBulkApproveDiscoveries,
  type DiscoveredArtifact,
} from '@/hooks/useDiscoveredArtifacts';
import {
  Search,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Inbox,
  Clock,
  Sparkles,
  Book,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

const SKILL_DEPTH_COLORS: Record<string, string> = {
  foundation: 'bg-green-100 text-green-800',
  applied: 'bg-blue-100 text-blue-800',
  advanced: 'bg-purple-100 text-purple-800',
  deep_dive: 'bg-indigo-100 text-indigo-800',
};

// Blocked domains for purchase URLs (client-side validation mirrors server-side)
const BLOCKED_PURCHASE_PATTERNS = [
  'books.google',
  'google.com/books',
  'google.de/books',
  'isbn.org',
  'isbnsearch.org',
  'bookfinder.com',
  'worldcat.org',
];

function isValidPurchaseUrl(url: string): { valid: boolean; reason?: string } {
  if (!url.trim()) return { valid: false, reason: 'URL is required' };
  
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    const fullUrl = url.toLowerCase();
    
    // Check blocked patterns
    for (const pattern of BLOCKED_PURCHASE_PATTERNS) {
      if (hostname.includes(pattern) || fullUrl.includes(pattern)) {
        return { valid: false, reason: `${pattern} URLs cannot be used as purchase URLs` };
      }
    }
    
    // Check Amazon search patterns
    if (hostname.includes('amazon.') && (fullUrl.includes('/s?') || fullUrl.includes('k='))) {
      return { valid: false, reason: 'Amazon search URLs cannot be used - use product page URLs' };
    }
    
    return { valid: true };
  } catch {
    return { valid: false, reason: 'Invalid URL format' };
  }
}

export function DiscoveryReviewTab() {
  const [filterStatus, setFilterStatus] = useState<'pending' | 'classified' | 'failed' | undefined>(undefined);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState<DiscoveredArtifact | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvingArtifactId, setApprovingArtifactId] = useState<string | null>(null);
  
  // Book approval state
  const [purchaseUrl, setPurchaseUrl] = useState('');
  const [purchaseProviderName, setPurchaseProviderName] = useState('');
  const [isReferenceOnly, setIsReferenceOnly] = useState(false);
  const [urlValidation, setUrlValidation] = useState<{ valid: boolean; reason?: string }>({ valid: true });

  // Bulk approval state
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [bulkApproveProgress, setBulkApproveProgress] = useState({ current: 0, total: 0 });

  // Drag-to-pan state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const { data: discoveries, isLoading } = usePendingDiscoveries({
    classificationStatus: filterStatus,
    limit: 50,
  });
  const { data: stats } = useDiscoveryStats();

  const approveDiscovery = useApproveDiscovery();
  const rejectDiscovery = useRejectDiscovery();
  const bulkApprove = useBulkApproveDiscoveries();

  // Filter discoveries that can be bulk approved (non-books only)
  const bulkApprovableArtifacts = discoveries?.filter(
    d => d.artifact_type !== 'book'
  ) || [];
  const booksCount = discoveries?.filter(d => d.artifact_type === 'book').length || 0;

  // Drag-to-pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    // Don't start drag if clicking on interactive elements
    if ((e.target as HTMLElement).closest('button, a, input, [role="button"]')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.pageX,
      y: e.pageY,
      scrollLeft: scrollContainerRef.current.scrollLeft,
      scrollTop: scrollContainerRef.current.scrollTop,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const dx = e.pageX - dragStart.x;
    const dy = e.pageY - dragStart.y;
    scrollContainerRef.current.scrollLeft = dragStart.scrollLeft - dx;
    scrollContainerRef.current.scrollTop = dragStart.scrollTop - dy;
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleApproveClick = (artifact: DiscoveredArtifact) => {
    if (artifact.artifact_type === 'book') {
      // Open dialog for book-specific fields
      setSelectedArtifact(artifact);
      setPurchaseUrl('');
      setPurchaseProviderName('');
      setIsReferenceOnly(false);
      setUrlValidation({ valid: true });
      setApproveDialogOpen(true);
    } else {
      // Direct approve for non-books
      handleApproveConfirm(artifact);
    }
  };

  const handleApproveConfirm = async (artifact?: DiscoveredArtifact) => {
    const targetArtifact = artifact || selectedArtifact;
    if (!targetArtifact) return;

    const isBook = targetArtifact.artifact_type === 'book';
    
    // Build overrides for books
    const overrides: Record<string, any> = {};
    if (isBook) {
      if (isReferenceOnly) {
        overrides.is_reference_only = true;
      } else {
        overrides.purchase_url = purchaseUrl;
        overrides.purchase_provider_name = purchaseProviderName;
      }
      // Pass through ISBNs if available
      if (targetArtifact.isbn_13) overrides.isbn_13 = targetArtifact.isbn_13;
      if (targetArtifact.isbn_10) overrides.isbn_10 = targetArtifact.isbn_10;
    }

    setApprovingArtifactId(targetArtifact.id);
    try {
      await approveDiscovery.mutateAsync({ 
        discoveredArtifactId: targetArtifact.id,
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
      });
      toast.success('Artifact approved and added to catalog');
      setApproveDialogOpen(false);
      setSelectedArtifact(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to approve artifact');
    } finally {
      setApprovingArtifactId(null);
    }
  };

  const handleRejectClick = (artifact: DiscoveredArtifact) => {
    setSelectedArtifact(artifact);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedArtifact) return;

    try {
      await rejectDiscovery.mutateAsync({
        discoveredArtifactId: selectedArtifact.id,
        reason: rejectReason || undefined,
      });
      toast.success('Artifact rejected');
      setRejectDialogOpen(false);
      setSelectedArtifact(null);
    } catch (error) {
      toast.error('Failed to reject artifact');
    }
  };

  // Validate purchase URL on change
  const handlePurchaseUrlChange = (value: string) => {
    setPurchaseUrl(value);
    if (value.trim()) {
      setUrlValidation(isValidPurchaseUrl(value));
    } else {
      setUrlValidation({ valid: true });
    }
  };

  // Check if book approval is valid
  const isBookApprovalValid = () => {
    if (isReferenceOnly) return true;
    if (!purchaseUrl.trim()) return false;
    if (!purchaseProviderName.trim()) return false;
    if (!urlValidation.valid) return false;
    return true;
  };

  // Bulk approve handler
  const handleBulkApprove = async () => {
    const ids = bulkApprovableArtifacts.map(a => a.id);
    if (ids.length === 0) return;

    setBulkApproveProgress({ current: 0, total: ids.length });
    
    try {
      const result = await bulkApprove.mutateAsync({
        artifactIds: ids,
        onProgress: (current, total) => {
          setBulkApproveProgress({ current, total });
        },
      });
      
      setBulkApproveDialogOpen(false);
      
      if (result.failed > 0) {
        toast.warning(`Approved ${result.approved} artifacts. ${result.failed} failed.`);
      } else {
        let message = `Approved ${result.approved} artifacts`;
        if (booksCount > 0) {
          message += `. ${booksCount} books skipped (require individual approval)`;
        }
        toast.success(message);
      }
    } catch (error) {
      toast.error('Bulk approval failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Discovery Review
              </CardTitle>
              <CardDescription>
                Review and approve/reject discovered training content
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {stats && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="outline">{stats.pending} pending</Badge>
                  <Badge variant="secondary">{stats.promoted} approved</Badge>
                </div>
              )}
              <Select
                value={filterStatus || 'all'}
                onValueChange={(v) => setFilterStatus(v === 'all' ? undefined : v as any)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pending</SelectItem>
                  <SelectItem value="classified">Classified</SelectItem>
                  <SelectItem value="pending">Awaiting Classification</SelectItem>
                  <SelectItem value="failed">Classification Failed</SelectItem>
                </SelectContent>
              </Select>
              {bulkApprovableArtifacts.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setBulkApproveDialogOpen(true)}
                  className="gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve All ({bulkApprovableArtifacts.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!discoveries || discoveries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No pending discoveries to review</p>
              <p className="text-sm mt-2">
                Discoveries appear here when providers fetch new content
              </p>
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className={cn(
                "h-[500px] overflow-auto",
                isDragging ? "cursor-grabbing" : "cursor-grab"
              )}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              <div className="space-y-4 pr-4 min-w-max">
                {discoveries.map((artifact) => (
                  <DiscoveryCard
                    key={artifact.id}
                    artifact={artifact}
                    onApprove={() => handleApproveClick(artifact)}
                    onReject={() => handleRejectClick(artifact)}
                    isApproving={approvingArtifactId === artifact.id}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Discovery</DialogTitle>
            <DialogDescription>
              This will mark "{selectedArtifact?.title}" as rejected. It won't appear again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., Duplicate content, Low quality, Off-topic"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={rejectDiscovery.isPending}
            >
              {rejectDiscovery.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Approval Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Book className="h-5 w-5" />
              Approve Book
            </DialogTitle>
            <DialogDescription>
              {selectedArtifact?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* ISBN Display */}
            {(selectedArtifact?.isbn_13 || selectedArtifact?.isbn_10) && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">ISBN</p>
                {selectedArtifact?.isbn_13 && <p>ISBN-13: {selectedArtifact.isbn_13}</p>}
                {selectedArtifact?.isbn_10 && <p>ISBN-10: {selectedArtifact.isbn_10}</p>}
              </div>
            )}

            {/* Google Books notice */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <p className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>Google Books URL is for metadata only.</span>
              </p>
              <p className="mt-1 text-xs">Enter the official product page URL where users can purchase this book.</p>
            </div>

            {/* Reference Only Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="reference-only"
                checked={isReferenceOnly}
                onCheckedChange={(checked) => setIsReferenceOnly(checked === true)}
              />
              <Label htmlFor="reference-only" className="text-sm">
                Reference material – not publicly sold
              </Label>
            </div>

            {!isReferenceOnly && (
              <>
                {/* Purchase URL */}
                <div className="space-y-2">
                  <Label htmlFor="purchase-url">Purchase URL *</Label>
                  <Input
                    id="purchase-url"
                    value={purchaseUrl}
                    onChange={(e) => handlePurchaseUrlChange(e.target.value)}
                    placeholder="https://www.aeswave.com/product-page.html"
                    className={cn(!urlValidation.valid && purchaseUrl && 'border-destructive')}
                  />
                  {!urlValidation.valid && purchaseUrl && (
                    <p className="text-xs text-destructive">{urlValidation.reason}</p>
                  )}
                </div>

                {/* Purchase Provider Name */}
                <div className="space-y-2">
                  <Label htmlFor="purchase-provider">Seller Name *</Label>
                  <Input
                    id="purchase-provider"
                    value={purchaseProviderName}
                    onChange={(e) => setPurchaseProviderName(e.target.value)}
                    placeholder="e.g., AESwave"
                  />
                  <p className="text-xs text-muted-foreground">
                    This is the store/seller name, not the author. Button will say "Buy from {purchaseProviderName || '...'}"
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleApproveConfirm()}
              disabled={approveDiscovery.isPending || !isBookApprovalValid()}
            >
              {approveDiscovery.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Approve Confirmation Dialog */}
      <Dialog open={bulkApproveDialogOpen} onOpenChange={setBulkApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve All Discoveries</DialogTitle>
            <DialogDescription>
              This will approve {bulkApprovableArtifacts.length} classified artifacts and add them to the training catalog.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>{bulkApprovableArtifacts.length} videos/podcasts will be approved</span>
            </div>
            
            {booksCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <Book className="h-4 w-4" />
                <span>{booksCount} books will be skipped (require purchase URL)</span>
              </div>
            )}
            
            {bulkApprove.isPending && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Approving {bulkApproveProgress.current} of {bulkApproveProgress.total}...</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${(bulkApproveProgress.current / bulkApproveProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setBulkApproveDialogOpen(false)}
              disabled={bulkApprove.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkApprove}
              disabled={bulkApprove.isPending || bulkApprovableArtifacts.length === 0}
            >
              {bulkApprove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve {bulkApprovableArtifacts.length} Artifacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Discovery Card - METADATA ONLY
 * Shows: title, thumbnail, duration, inferred domains/skill, canonical URL, ISBN for books
 * Does NOT show: transcripts, descriptions, extracted content
 */
function DiscoveryCard({
  artifact,
  onApprove,
  onReject,
  isApproving,
}: {
  artifact: DiscoveredArtifact;
  onApprove: () => void;
  onReject: () => void;
  isApproving: boolean;
}) {
  const providerName = (artifact.source as any)?.provider?.display_name || 'Unknown Provider';
  const domains = Array.isArray(artifact.inferred_domains) ? artifact.inferred_domains as string[] : [];
  const skillDepth = artifact.inferred_skill_depth;
  const isBook = artifact.artifact_type === 'book';

  return (
    <div className="border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow min-w-[320px]">
      <div className="flex gap-4">
        {/* Thumbnail */}
        {artifact.thumbnail_url ? (
          <img
            src={artifact.thumbnail_url}
            alt=""
            className="w-24 h-16 object-cover rounded flex-shrink-0"
          />
        ) : (
          <div className="w-24 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
            {isBook ? (
              <Book className="h-6 w-6 text-muted-foreground" />
            ) : (
              <Sparkles className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row - stacks on narrow screens */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-medium truncate">{artifact.title}</h4>
              <p className="text-sm text-muted-foreground">{providerName}</p>
            </div>
            {/* Action buttons - always visible */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                asChild
                title="Open original content"
              >
                <a href={artifact.canonical_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={onReject}
                title="Reject"
              >
                <XCircle className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={onApprove}
                disabled={isApproving}
                className="gap-1"
              >
                {isApproving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Approve</span>
              </Button>
            </div>
          </div>

          {/* Metadata badges */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {/* Type badge */}
            {isBook && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                <Book className="h-3 w-3 mr-1" />
                Book
              </Badge>
            )}

            {/* Duration */}
            {artifact.duration_minutes && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {artifact.duration_minutes} min
              </Badge>
            )}

            {/* ISBN for books */}
            {isBook && artifact.isbn_13 && (
              <Badge variant="outline" className="text-xs font-mono">
                ISBN: {artifact.isbn_13}
              </Badge>
            )}

            {/* Classification status */}
            <Badge
              variant={artifact.classification_status === 'classified' ? 'default' : 'secondary'}
              className="text-xs"
            >
              {artifact.classification_status || 'pending'}
            </Badge>

            {/* Skill depth */}
            {skillDepth && (
              <Badge
                className={cn('text-xs', SKILL_DEPTH_COLORS[skillDepth])}
                variant="secondary"
              >
                {skillDepth.replace(/_/g, ' ')}
              </Badge>
            )}

            {/* Inferred domains (first 2) */}
            {domains.slice(0, 2).map((domain) => (
              <Badge key={domain} variant="outline" className="text-xs">
                {domain.replace(/_/g, ' ')}
              </Badge>
            ))}
            {domains.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{domains.length - 2} more
              </Badge>
            )}

            {/* Discovered time */}
            <span className="text-xs text-muted-foreground ml-auto">
              {formatDistanceToNow(new Date(artifact.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiscoveryReviewTab;
